import { Link } from 'react-router-dom';

export default function Pricing() {
    return (
        <div className="bg-white font-body">
            <div className="py-24">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-primary-900 mb-4">Simple, Transparent Pricing</h1>
                        <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
                            Choose the plan that fits your practice. No hidden fees, cancel anytime.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto">
                        {/* Basic Plan */}
                        <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 sm:p-8 hover:border-primary-300 transition-all duration-300">
                            <div className="mb-6">
                                <h3 className="font-display text-2xl font-bold text-primary-900">Basic</h3>
                                <p className="text-gray-600 mt-2 text-sm">For attorneys getting started with expert witness search</p>
                            </div>
                            <div className="mb-8">
                                <span className="text-4xl sm:text-5xl font-bold text-primary-900">$25</span>
                                <span className="text-gray-600">/month</span>
                            </div>
                            <ul className="space-y-3 mb-8">
                                {['Search verified medical experts', 'Contact up to 3 experts per month', 'Post 1 case listing per month', 'Anonymous messaging', 'Standard support'].map((f, i) => (
                                    <li key={i} className="flex items-start">
                                        <svg className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        <span className="text-gray-700 text-sm">{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <Link to="/signup" className="block w-full text-center px-6 py-3 bg-white border-2 border-primary-800 text-primary-800 rounded-xl font-semibold hover:bg-primary-50 transition-all duration-300">
                                Get Started
                            </Link>
                        </div>

                        {/* Founding Member Plan */}
                        <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-2xl border-2 border-amber-400 p-6 sm:p-8 relative overflow-hidden">
                            <div className="absolute top-4 right-4 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                                LIMITED TIME
                            </div>
                            <div className="mb-6">
                                <h3 className="font-display text-2xl font-bold text-amber-900">Founding Member</h3>
                                <p className="text-amber-700 mt-2 text-sm">Lock in unlimited access at a special introductory rate — forever</p>
                            </div>
                            <div className="mb-2">
                                <span className="text-4xl sm:text-5xl font-bold text-amber-900">$49</span>
                                <span className="text-amber-700">/month</span>
                            </div>
                            <p className="text-sm text-amber-600 mb-6 font-medium">
                                <span className="line-through text-gray-400">$129/month</span> — Price locked forever
                            </p>
                            <ul className="space-y-3 mb-8">
                                {['Everything in Unlimited', '$49/month locked in for life', 'Founding Member badge', 'Unlimited expert contacts', 'Unlimited case postings', 'Priority support', 'Early access to new features'].map((f, i) => (
                                    <li key={i} className="flex items-start">
                                        <svg className="w-5 h-5 text-amber-500 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        <span className="text-gray-800 text-sm font-medium">{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <Link to="/signup" className="block w-full text-center px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl">
                                Become a Founding Member
                            </Link>
                            <p className="text-xs text-amber-700 text-center mt-3">Available for a limited time only. Once this offer ends, the regular $129/month rate applies.</p>
                        </div>

                        {/* Unlimited Plan */}
                        <div className="bg-gradient-to-br from-primary-900 to-primary-800 rounded-2xl p-6 sm:p-8 text-white relative overflow-hidden">
                            <div className="mb-6">
                                <h3 className="font-display text-2xl font-bold">Unlimited</h3>
                                <p className="text-primary-200 mt-2 text-sm">For firms that need unlimited access to expert witnesses</p>
                            </div>
                            <div className="mb-8">
                                <span className="text-4xl sm:text-5xl font-bold">$129</span>
                                <span className="text-primary-200">/month</span>
                            </div>
                            <ul className="space-y-3 mb-8">
                                {['Everything in Basic', 'Unlimited expert contacts', 'Unlimited case postings', 'Priority matching algorithm', 'Priority support'].map((f, i) => (
                                    <li key={i} className="flex items-start">
                                        <svg className="w-5 h-5 text-accent-400 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                        <span className="text-sm">{f}</span>
                                    </li>
                                ))}
                            </ul>
                            <Link to="/signup" className="block w-full text-center px-6 py-3 bg-accent-500 hover:bg-accent-600 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl">
                                Get Started
                            </Link>
                        </div>
                    </div>

                    {/* Expert Note */}
                    <div className="mt-16 text-center">
                        <div className="bg-gray-50 rounded-2xl p-8 max-w-2xl mx-auto">
                            <h3 className="font-display text-xl font-bold text-primary-900 mb-2">Are you a medical expert?</h3>
                            <p className="text-gray-600 mb-4">
                                Expert Link Pro is free for medical professionals. Create your profile, get verified, and start receiving case inquiries from attorneys.
                            </p>
                            <Link to="/signup" className="inline-flex items-center px-6 py-3 bg-primary-800 text-white rounded-xl font-semibold hover:bg-primary-900 transition-all duration-300">
                                Join as an Expert — Free
                            </Link>
                        </div>
                    </div>

                    <div className="mt-8 text-center">
                        <p className="text-gray-500 text-sm">Questions? Contact us at <a href="mailto:support@expertlinkpro.com" className="text-blue-600 hover:text-blue-800 font-medium">support@expertlinkpro.com</a></p>
                    </div>
                </div>
            </div>
        </div>
    );
}
